import { useCallback, useEffect, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { chartService } from '@chartai/firebase';
import { type Chart } from '@chartai/types';

import { chartKeys } from '../queryKeys';

export function useCharts(userId: string) {
  return useQuery({
    queryKey: chartKeys.list({ userId }),
    queryFn: async () => {
      return chartService.getChartsByUser(userId);
    },
    enabled: Boolean(userId),
  });
}

export function useChartsByPatient(userId: string, patientId: string) {
  return useQuery({
    queryKey: chartKeys.list({ userId, patientId }),
    queryFn: async () => {
      return chartService.getChartsByPatient(userId, patientId);
    },
    enabled: Boolean(userId) && Boolean(patientId),
  });
}

export function useAllChartsByUser(userId: string) {
  return useQuery({
    queryKey: chartKeys.list({ userId, scope: 'all' }),
    queryFn: async () => {
      return chartService.getAllChartsByUser(userId);
    },
    enabled: Boolean(userId),
  });
}

// CAB-420: live subscription. Reprocess and pipeline writes land on the
// chart doc 1–2 minutes after the user clicks Reprocess; without an
// `onSnapshot` listener the ChartDetailPage was stuck on the snapshot it
// fetched right after the click (status: "Processing", missing new
// `notesByTemplate.<X>` keys) until a manual reload. Returns the same
// `{ data, isLoading, isError, refetch }` shape useQuery did so callers
// don't change. `refetch` tears down the current snapshot listener and
// re-establishes it — useful for the mobile error-recovery retry buttons.
// Derived so api-client doesn't need to import `DocumentData` from
// `firebase/firestore` directly (package boundary rule). Matches what the
// old `useQuery({ queryFn: getChart })` inferred, so existing call sites
// that read `chart['summary'] ?? ''` keep their loose typing and don't
// trigger `@typescript-eslint/no-base-to-string`.
type ChartDoc = Parameters<Parameters<typeof chartService.subscribeToChart>[1]>[0];

export function useChart(chartId: string) {
  const [data, setData] = useState<ChartDoc | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [subVersion, setSubVersion] = useState<number>(0);

  useEffect(() => {
    if (!chartId) {
      setData(undefined);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setIsError(false);

    try {
      const unsubscribe = chartService.subscribeToChart(chartId, (chart) => {
        setData(chart);
        setIsLoading(false);
      });
      return unsubscribe;
    } catch {
      setIsError(true);
      setIsLoading(false);
    }

    return undefined;
  }, [chartId, subVersion]);

  const refetch = useCallback(() => {
    setSubVersion((v) => v + 1);
  }, []);

  return { data, isLoading, isError, refetch };
}

// CAB-540: live subscription to the latest `jobs/{chartId}/summary` entry —
// the source of truth for the current clinical note. The root chart doc's
// `summary` / `notesByTemplate` can lag behind an admin edit (the Angular admin
// only triggers the backend to append to this subcollection), so the page reads
// the displayed note from here. Mirrors `useChart`'s shape and teardown.
// `null` data means the subcollection is empty (legacy/new chart) → caller
// falls back to the root-doc note.
type LatestSummaryDoc = Parameters<Parameters<typeof chartService.subscribeToLatestSummary>[1]>[0];

export function useLatestSummary(chartId: string) {
  const [data, setData] = useState<LatestSummaryDoc | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);

  useEffect(() => {
    if (!chartId) {
      setData(undefined);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setIsError(false);

    try {
      const unsubscribe = chartService.subscribeToLatestSummary(
        chartId,
        (summary) => {
          setData(summary);
          setIsLoading(false);
        },
        // CAB-544: a permission-denied (or any listener failure) is delivered
        // asynchronously, so the sync catch below never sees it. Flag isError so
        // the page can fall back to the root-doc note; the service already
        // reports `null` data for the same reason.
        () => {
          setIsError(true);
          setIsLoading(false);
        },
      );
      return unsubscribe;
    } catch {
      setIsError(true);
      setIsLoading(false);
    }

    return undefined;
  }, [chartId]);

  return { data, isLoading, isError };
}

export function useCreateChart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      title: string;
      patientUID: string;
      uid: string;
      staffUID: string;
      profileUID: string;
      owner: string;
      summaryType: string;
      date: Date;
      requireConsent: boolean;
      customTemplateId?: string;
    }) => {
      const chartId = await chartService.createChartEntry(input);

      // Non-blocking: save debug info for troubleshooting
      try {
        const debugInfo = chartService.collectDebugInfo();
        await chartService.saveCriticalDebugInfo(chartId, debugInfo);
      } catch {
        // Debug collection should never block chart creation
        console.error('[CreateChart] Failed to save debug info');
      }

      return chartId;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chartKeys.lists() });
    },
  });
}

export function useUploadChartAudio() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      chartId: string;
      userId: string;
      audioBlob: Blob;
      onProgress?: (percent: number) => void;
    }) => {
      return chartService.uploadChartAudio(input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chartKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: chartKeys.details() });
    },
  });
}

export function useUploadChartAudioBase64() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      chartId: string;
      userId: string;
      base64: string;
      mimeType: string;
    }) => {
      return chartService.uploadChartAudioBase64(input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chartKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: chartKeys.details() });
    },
  });
}

/**
 * Upload audio checkpoint (for pause functionality).
 * Saves audio without triggering transcription.
 */
export function useUploadChartAudioCheckpoint() {
  return useMutation({
    mutationFn: async (input: {
      chartId: string;
      userId: string;
      audioBlob: Blob;
      onProgress?: (percent: number) => void;
    }) => {
      return chartService.uploadChartAudioCheckpoint(input);
    },
  });
}

/**
 * Download checkpoint audio for resume functionality.
 */
export function useDownloadChartAudioCheckpoint() {
  return useMutation({
    mutationFn: async (checkpointPath: string) => {
      return chartService.downloadChartAudioCheckpoint(checkpointPath);
    },
  });
}

export function useUpdateChart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { chartId: string; data: Record<string, unknown> }) => {
      await chartService.updateChart(input.chartId, input.data);
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: chartKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: chartKeys.detail(variables.chartId) });
    },
  });
}

export function useDeleteChart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (chartId: string) => {
      await chartService.deleteChart(chartId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chartKeys.lists() });
    },
  });
}

/**
 * Records whether the user finished uploading the chart's audio (CAB-502).
 * Set `false` at recording start and `true` at upload completion. This is
 * telemetry on the debug doc, so it does not invalidate chart queries.
 */
export function useSetUploadFinishedFlag() {
  return useMutation({
    mutationFn: async (input: { chartId: string; finished: boolean }) => {
      await chartService.setUploadFinishedFlag(input.chartId, input.finished);
    },
  });
}

interface DateRangeParams {
  userId: string;
  profileId: string;
  startDate: string;
  endDate: string;
}

export function useChartsByDateRange(params: DateRangeParams) {
  const { userId, profileId, startDate, endDate } = params;

  return useQuery<Chart[]>({
    queryKey: chartKeys.list({ userId, profileId, startDate, endDate }),
    queryFn: async () => {
      const docs = await chartService.getChartsByDateRange(userId, profileId, startDate, endDate);
      return docs as Chart[];
    },
    enabled: Boolean(userId) && Boolean(profileId) && Boolean(startDate) && Boolean(endDate),
  });
}

interface OrgDateRangeParams {
  officeIds: string[];
  startDate: string;
  endDate: string;
}

export function useOrgChartsByDateRange(params: OrgDateRangeParams) {
  const { officeIds, startDate, endDate } = params;

  return useQuery<Chart[]>({
    queryKey: chartKeys.list({ scope: 'org', officeIds, startDate, endDate }),
    queryFn: async () => {
      const docs = await chartService.getOrgChartsByDateRange(officeIds, startDate, endDate);
      return docs as Chart[];
    },
    enabled: officeIds.length > 0 && Boolean(startDate) && Boolean(endDate),
  });
}

/** Real-time subscription: charts for a specific profile */
export function useLiveChartsByProfile(userId: string, profileId: string) {
  const [data, setData] = useState<Array<Record<string, unknown> & { id: string }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!userId || !profileId) {
      setData([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setIsError(false);

    try {
      const unsubscribe = chartService.subscribeToChartsByProfile(userId, profileId, (docs) => {
        const filtered = docs.filter((d) => (d as Record<string, unknown>)['deleted'] !== true);
        setData(filtered as Array<Record<string, unknown> & { id: string }>);
        setIsLoading(false);
      });
      return unsubscribe;
    } catch {
      setIsError(true);
      setIsLoading(false);
    }

    return undefined;
  }, [userId, profileId]);

  return { data, isLoading, isError };
}

/**
 * Returns the count of jobs the user created since a given date.
 * Used by the dashboard StatsBanner "Time Saved This Week" card. Uses
 * Firestore's server-side count aggregation — cheap regardless of how
 * many jobs the user has.
 */
export function useJobCountSince(userId: string, since: Date) {
  const sinceMs = since.getTime();
  return useQuery({
    queryKey: chartKeys.list({ userId, scope: 'count-since', sinceMs }),
    queryFn: async () => {
      return chartService.countJobsSince(userId, since);
    },
    enabled: Boolean(userId),
    // Refresh every 5 min so the dashboard reflects new charts without
    // forcing a full page reload.
    staleTime: 5 * 60 * 1000,
  });
}

/** Real-time subscription: all charts for a user */
export function useLiveAllChartsByUser(userId: string) {
  const [data, setData] = useState<Array<Record<string, unknown> & { id: string }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!userId) {
      setData([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setIsError(false);

    try {
      const unsubscribe = chartService.subscribeToAllChartsByUser(userId, (docs) => {
        const filtered = docs.filter((d) => (d as Record<string, unknown>)['deleted'] !== true);
        setData(filtered as Array<Record<string, unknown> & { id: string }>);
        setIsLoading(false);
      });
      return unsubscribe;
    } catch {
      setIsError(true);
      setIsLoading(false);
    }

    return undefined;
  }, [userId]);

  return { data, isLoading, isError };
}
